import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from '../../context';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import "rc-switch/assets/index.css";
import { Nav, NavItem, TabContent, TabPane, Row, Col } from "reactstrap";
import { NavLink } from "react-router-dom";
import classnames from "classnames";
import { asyncHandler, updateLocalValue, addLocalValue, deleteLocalValue, rackUpdate, notify } from '../../utils';
import { v4 as uuidv4 } from 'uuid';

import AppLayout from '../../components/AppLayout';
import Search from '../../components/import/rack/Search.js';
import View from '../../components/import/rack/View.js';
import ModalEditData from '../../components/import/rack/ModalEditData';
import useRackData from '../../components/import/rack/useRackData';
import Coolers from '../../components/import/rack/Coolers';
import Deleted from '../../components/import/rack/Deleted';
import Customs from '../../components/import/rack/Customs';
import ModalConfirmDelete from '../../components/import/rack/ModalConfirmDelete';
import { useSearchAwb } from '../../components/searchAwb/useSearchAwb';
import apiClient from '../../apiClient';
import useLoading from '../../customHooks/useLoading';

const Rack = ({
    user
}) => {

    const { setLoading } = useLoading();
    const { handleSearchAwb } = useSearchAwb();
    const [activeTab, setActiveTab] = useState('1');
    const [newLocation, setNewLocation] = useState(false);
    const [selectedRackItem, setSelectedRackItem] = useState(null);
    const [modalEditOpen, setModalEditOpen] = useState(false);
    const [modalConfirmDelete, setModalConfirmDelete] = useState(false);

    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    }

    const {
        rackItems,
        setRackItems,
        schema,
        specialLocations,
        locationsMap,
        updateRackData
    } = useRackData(user.s_unit);

    const handleAddUpdate = (newLocation, rackItem = {}) => {
        setNewLocation(newLocation);
        setSelectedRackItem(rackItem);
        setModalEditOpen(true);
    }

    const resolveLocation = (s_tower, s_level, s_position) => {
        // const position = parseInt(s_position) < 10 ? `0${s_position}` : s_position;
        return `${s_tower}${s_level}${s_position}`;
    }

    const resolveSpecialHandlingCodeString = (values) => {
        let s_special_hanlding_code = '';
        for (let i = 1; i <= 5; i++) {
            s_special_hanlding_code += `${values[`s_shc${i}`]}, `;
        }
        return s_special_hanlding_code.substring(0, s_special_hanlding_code.length - 2);
    }

    const mailLocationLogic = (s_location, s_unit) => {
        let b_delivered = false,
            s_status = 'LOCATED';

        if (s_location === 'MAIL' && s_unit === 'CEWR1') {
            b_delivered = true;
            s_status = 'DELIVERED';
        }

        return {
            b_delivered,
            s_status
        };
    }

    const updateRackLocation = async (values) => {
        try {
            setLoading(true);
            values.s_unit = user && user.s_unit;
            values.s_location = values.enableSpecialLocation ?
                values.s_location : resolveLocation(values.s_tower, values.s_level, values.s_position);

            const now = moment().local().format('MM/DD/YYYY hh:mm A');
            values.t_modified = now;
            values.s_modified_by = user && user.s_email;
            values.s_location_type = values.enableSpecialLocation ? 'SPECIAL' : 'REGULAR';
            values.s_mawb = values.s_mawb.replace(/-/g, '');
            values.s_hawb = values.s_hawb.replace(/\s/g, '');

            const userName = user.s_email.toUpperCase().split('@')[0];
            const date = moment().format('MM/DD/YYYY');
            const formatNotes = values.s_notes && values.s_notes.length > 0 ? `[${userName}|${date} - ${values.s_notes}]. ` : '';
            values.s_notes = formatNotes;

            const { b_delivered, s_status } = mailLocationLogic(values.s_location, user.s_unit);
            const existingDeliveredStatus = selectedRackItem.b_delivered === true || selectedRackItem.s_status === 'DELIVERED';
            values.b_delivered = existingDeliveredStatus || b_delivered;
            values.s_status = existingDeliveredStatus ? 'DELIVERED' : s_status;

            delete values.specialLocation;
            delete values.enableSpecialLocation;

            const response = await apiClient.post('/updateRackLocation', values);

            updateLocalValue(rackItems, setRackItems, selectedRackItem.id, response.data[0]);
            setModalEditOpen(false);
            notify('Location Updated');
            rackUpdate(values.s_mawb, user.s_unit);
            setSelectedRackItem({});
        } catch (err) {
            alert(err);
        } finally {
            setLoading(false);
        }
    }

    const updateRackStatus = asyncHandler(async (s_status, s_notes) => {
        const { id, s_location, s_mawb, s_created_by, t_created, i_pieces, s_unit } = selectedRackItem;

        const data = {
            id,
            s_location,
            s_mawb,
            s_status,
            i_pieces: i_pieces || 0,
            t_modified: moment().local().format('MM/DD/YYYY HH:mm:ss'),
            s_modified_by: user.s_email,
            s_created_by,
            t_created,
            b_delivered: (s_status === 'DELIVERED'),
            s_unit,
            s_notes
        }
        if (data.b_delivered !== true) {
            data.b_delivered = false;
        }

        await apiClient.post('/updateRackStatus', { data });

        setModalEditOpen(false);
        deleteLocalValue(rackItems, setRackItems, id);
        notify('Location Deleted');
        rackUpdate(s_mawb, user.s_unit);
    });

    const locateInRack = asyncHandler(async (values) => {
        const now = moment().local().format('MM/DD/YYYY HH:mm:ss');
        const s_location = values.enableSpecialLocation ?
            values.s_location : resolveLocation(values.s_tower, values.s_level, values.s_position);

        const { b_delivered, s_status } = mailLocationLogic(s_location, user.s_unit);

        values.t_created = now;
        values.t_modified = now;
        values.s_created_by = user.s_email;
        values.s_modified_by = user.s_email;
        values.s_unit = user && user.s_unit;
        values.s_location = values.enableSpecialLocation ?
            values.s_location : resolveLocation(values.s_tower, values.s_level, values.s_position);
        values.b_delivered = b_delivered;
        values.i_airline_prefix = values.s_mawb.substr(0, 3);
        values.s_priority = null;
        values.s_guid = uuidv4();
        values.s_flight_id = `${values.s_airline_code}${values.s_flightnumber}/${values.d_flight}`
        values.s_platform = 'EOS';
        values.s_status = s_status;
        values.s_mawb = values.s_mawb.replace(/-/g, '');
        values.s_destination = user.s_unit.substring(1, 4);
        values.s_procedure = 'controller: locateInRack';

        delete values.specialLocation;
        delete values.enableSpecialLocation;
        delete values.s_location_type;

        const data = {
            locate: values
        }

        const response = await apiClient.post('/locateInRack', { data });

        notify('AWB Located');
        rackUpdate(values.s_mawb, user.s_unit);
        setModalEditOpen(false);
        addLocalValue(rackItems, setRackItems, response.data);
    });

    return (
        <AppLayout>
            <div className={`card queue-card-container`} style={{ backgroundColor: '#f8f8f8', height: 'calc(100vh - 120px)', overflowY: 'scroll', overflowX: 'hidden' }}>
                <div className="card-body mb-5 px-1 py-1">
                    <Row className='py-3'>
                        <Col md='12' lg='12'>
                            <Row>
                                <Col md={12}>
                                    <h1 className='pl-3 pb-0 mb-1 float-left'>Rack</h1>
                                    {
                                        user.i_access_level >= 5 &&
                                        <i
                                            className="fas fa-plus-circle float-right text-large text-primary hover-pointer mr-3"
                                            data-tip={'Add Location'}
                                            onClick={() => handleAddUpdate(true)}
                                        ></i>
                                    }
                                </Col>
                            </Row>
                            <Row>
                                <Col md='12' className={'mx-2'}>
                                    <Nav tabs className="separator-tabs ml-0 mb-2">
                                        <NavItem>
                                            <NavLink
                                                location={{}}
                                                to="#"
                                                className={classnames({
                                                    active: activeTab === "1",
                                                    "nav-link": true
                                                })}
                                                onClick={() => {
                                                    toggleTab("1");
                                                }}
                                            >
                                                Search
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                location={{}}
                                                to="#"
                                                className={classnames({
                                                    active: activeTab === "2",
                                                    "nav-link": true
                                                })}
                                                onClick={() => {
                                                    toggleTab("2");
                                                }}
                                            >
                                                View
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                location={{}}
                                                to="#"
                                                className={classnames({
                                                    active: activeTab === "3",
                                                    "nav-link": true
                                                })}
                                                onClick={() => {
                                                    toggleTab("3");
                                                }}
                                            >
                                                Coolers
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                location={{}}
                                                to="#"
                                                className={classnames({
                                                    active: activeTab === "LOGGED",
                                                    "nav-link": true
                                                })}
                                                onClick={() => {
                                                    toggleTab("LOGGED");
                                                }}
                                            >
                                                Logged
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                location={{}}
                                                to="#"
                                                className={classnames({
                                                    active: activeTab === "CUSTOMS",
                                                    "nav-link": true
                                                })}
                                                onClick={() => {
                                                    toggleTab("CUSTOMS");
                                                }}
                                            >
                                                Customs
                                            </NavLink>
                                        </NavItem>
                                    </Nav>
                                </Col>
                            </Row>

                            <TabContent activeTab={activeTab} className='mt-2'>
                                <TabPane tabId="1">
                                    <Search
                                        rackItems={rackItems}
                                        handleAddUpdate={handleAddUpdate}
                                        notify={notify}
                                        locationsMap={locationsMap}
                                        user={user}
                                    />
                                </TabPane>
                                <TabPane tabId="2">
                                    <View
                                        schema={schema}
                                        specialLocations={specialLocations}
                                        rackItems={rackItems}
                                        handleAddUpdate={handleAddUpdate}
                                    />
                                </TabPane>
                                <TabPane tabId="3">
                                    <Coolers />
                                </TabPane>
                                <TabPane tabId="LOGGED">
                                    <Deleted
                                        activeTab={activeTab}
                                    />
                                </TabPane>
                                <TabPane tabId="CUSTOMS">
                                    <Customs
                                        activeTab={activeTab}
                                        rackItems={rackItems}
                                        updateRackData={updateRackData}
                                    />
                                </TabPane>
                            </TabContent>
                        </Col>
                    </Row>
                </div>
            </div>

            <ModalEditData
                modal={modalEditOpen}
                setModal={setModalEditOpen}
                schema={schema}
                specialLocations={specialLocations}
                user={user}
                newLocation={newLocation}
                rackItem={selectedRackItem}
                rackItems={rackItems}
                locateInRack={locateInRack}
                updateRackLocation={updateRackLocation}
                updateRackStatus={updateRackStatus}
                locationsMap={locationsMap}
                handleSearchAwb={handleSearchAwb}
                setModalConfirmDelete={setModalConfirmDelete}
            />
            <ModalConfirmDelete
                modal={modalConfirmDelete}
                setModal={setModalConfirmDelete}
                selectedRackItem={selectedRackItem}
                updateRackStatus={updateRackStatus}
            />
        </AppLayout>
    );
}

export default withRouter(Rack);